import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'

type Callback = () => void

interface StoredCallback {
  token: symbol
  callback: Callback
}

interface RouteBackContextValue {
  registerRouteBack: (ownerKey: string, callback: Callback) => symbol
  unregisterRouteBack: (ownerKey: string, token: symbol) => void
  invokeRouteBack: (ownerKey: string) => boolean
}

const RouteBackContext = createContext<RouteBackContextValue | null>(null)

export function RouteBackProvider({ children }: { children: ReactNode }) {
  const callbacks = useRef(new Map<string, StoredCallback>())
  const registerRouteBack = useCallback((ownerKey: string, callback: Callback) => {
    const token = Symbol(ownerKey)
    callbacks.current.set(ownerKey, { token, callback })
    return token
  }, [])
  const unregisterRouteBack = useCallback((ownerKey: string, token: symbol) => {
    const current = callbacks.current.get(ownerKey)
    if (current?.token === token) callbacks.current.delete(ownerKey)
  }, [])
  const invokeRouteBack = useCallback((ownerKey: string) => {
    const current = callbacks.current.get(ownerKey)
    if (!current) return false
    current.callback()
    return true
  }, [])
  const value = useMemo(() => ({ registerRouteBack, unregisterRouteBack, invokeRouteBack }), [registerRouteBack, unregisterRouteBack, invokeRouteBack])
  return createElement(RouteBackContext.Provider, { value }, children)
}

export function useRouteBack() {
  const ctx = useContext(RouteBackContext)
  if (!ctx) throw new Error('useRouteBack must be used inside RouteBackProvider')
  return ctx
}

export function useOptionalRouteBack() {
  return useContext(RouteBackContext)
}

export function useRouteBackRegistration(ownerKey: string | null, callback: Callback): void {
  const ctx = useContext(RouteBackContext)
  useEffect(() => {
    if (!ownerKey || !ctx) return undefined
    const token = ctx.registerRouteBack(ownerKey, callback)
    return () => ctx.unregisterRouteBack(ownerKey, token)
  }, [callback, ctx, ownerKey])
}
